import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Power, Map, Loader2, CheckCircle2, XCircle, ShieldAlert } from 'lucide-react';
import mobileDeviceApi from '../services/mobileDeviceApi';
import { useAuth } from '../contexts/AuthContext';

export default function DeclaredPassSettings() {
    const [enabled, setEnabled] = useState(false);
    const [requireLocation, setRequireLocation] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    const { permissions } = useAuth();
    const canManageSettings =
        permissions.includes('settings:manage') ||
        permissions.includes('mobile_devices:manage_policy');

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const settings = await mobileDeviceApi.getSettings();

            const enabledSetting = settings.find(s => s.key === "DeclaredPass.Enabled");
            if (enabledSetting) {
                setEnabled(enabledSetting.value === "true");
            }

            const locationSetting = settings.find(s => s.key === "DeclaredPass.RequireLocation");
            if (locationSetting) {
                setRequireLocation(locationSetting.value === "true");
            }
        } catch (err) {
            console.error("Beyanlı geçiş ayarları alınamadı", err);
            setError("Beyanlı geçiş ayarları alınamadı.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleSetEnabled = async (targetState) => {
        if (loading || !canManageSettings || targetState === enabled) return;
        setLoading(true);
        setError(null);
        setMessage(null);
        try {
            await mobileDeviceApi.updateSetting("DeclaredPass.Enabled", targetState ? "true" : "false");
            setEnabled(targetState);

            // Eğer özellik kapatıldıysa, konumu da sıfırlayalım/kapatalım
            if (!targetState && requireLocation) {
                await mobileDeviceApi.updateSetting("DeclaredPass.RequireLocation", "false");
                setRequireLocation(false);
            }

            setMessage(`Beyanlı Geçiş özelliği ${targetState ? "aktif" : "pasif"} edildi.`);
        } catch (err) {
            setError(err?.message || "Ayar güncellenemedi.");
        } finally {
            setLoading(false);
        }
    };

    const handleSetLocation = async (targetState) => {
        if (loading || !canManageSettings || !enabled || targetState === requireLocation) return;
        setLoading(true);
        setError(null);
        setMessage(null);
        try {
            await mobileDeviceApi.updateSetting("DeclaredPass.RequireLocation", targetState ? "true" : "false");
            setRequireLocation(targetState);
            setMessage(`Beyanlı geçişte GPS konum zorunluluğu ${targetState ? "açıldı" : "kapatıldı"}.`);
        } catch (err) {
            setError(err?.message || "Konum ayarı güncellenemedi.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white dark:bg-[#18191c] p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-5">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                        <Map className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2.5 flex-wrap">
                            <h3 className="text-base font-bold text-gray-900 dark:text-white">
                                Beyanlı Geçiş Yönetimi
                            </h3>
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${
                                enabled
                                    ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                            }`}>
                                {enabled ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                {enabled ? 'Aktif' : 'Kapalı'}
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Saha veya uzaktan çalışan personellerin fiziksel turnike/cihaz olmadan mobil uygulama üzerinden kendi beyanlarıyla giriş/çıkış yapmasını yönetin.
                        </p>
                    </div>
                </div>
            </div>

            {/* Mesaj Bildirimleri */}
            {message && (
                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 rounded-xl border border-emerald-200 dark:border-emerald-800/50 text-xs font-medium mb-4 flex items-center justify-between">
                    <span>{message}</span>
                    <button type="button" onClick={() => setMessage(null)} className="text-emerald-500 hover:text-emerald-700 font-bold ml-2">✕</button>
                </div>
            )}
            {error && (
                <div className="p-3 bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 rounded-xl border border-rose-200 dark:border-rose-800/50 text-xs font-medium mb-4 flex items-center justify-between">
                    <span>{error}</span>
                    <button type="button" onClick={() => setError(null)} className="text-rose-500 hover:text-rose-700 font-bold ml-2">✕</button>
                </div>
            )}

            {/* Ayarlar Kutusu */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Beyanlı Geçiş Aktif Toggle */}
                <div className="flex flex-col justify-between p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800">
                    <div className="flex items-start gap-3 mb-4">
                        <div className={`p-2.5 rounded-xl shrink-0 ${
                            enabled
                                ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                                : 'bg-gray-200/70 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                        }`}>
                            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Power className="w-5 h-5" />}
                        </div>
                        <div>
                            <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100">Mobil Beyanlı Geçiş İzni</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                                {enabled
                                    ? 'Personeller mobil uygulamada "Beyanlı Giriş / Çıkış" butonlarını kullanarak hareket kaydı oluşturabilir.'
                                    : 'Personeller mobil uygulamadan beyanlı geçiş gönderemez; yalnızca QR/BLE veya fiziksel terminaller kullanılır.'}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-1.5 bg-gray-200/80 dark:bg-[#18191c] p-1 rounded-xl border border-gray-200 dark:border-gray-800">
                        <button
                            type="button"
                            onClick={() => handleSetEnabled(false)}
                            disabled={loading || !canManageSettings}
                            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                !enabled
                                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm border border-gray-200/80 dark:border-gray-700'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                            } ${!canManageSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            Kapalı
                        </button>
                        <button
                            type="button"
                            onClick={() => handleSetEnabled(true)}
                            disabled={loading || !canManageSettings}
                            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                enabled
                                    ? 'bg-emerald-600 text-white shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                            } ${!canManageSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            Açık (Aktif)
                        </button>
                    </div>
                </div>

                {/* Konum Zorunlu Ayarı */}
                <div className={`flex flex-col justify-between p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800 transition-opacity ${
                    !enabled ? 'opacity-60' : ''
                }`}>
                    <div className="flex items-start gap-3 mb-4">
                        <div className={`p-2.5 rounded-xl shrink-0 ${
                            enabled && requireLocation
                                ? 'bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400'
                                : 'bg-gray-200/70 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                        }`}>
                            <MapPin className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100">GPS Konum Zorunluluğu</h4>
                                {!enabled && (
                                    <span className="text-[10px] px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-semibold">
                                        Beyanlı Geçiş Kapalı
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                                {requireLocation
                                    ? 'Zorunlu: Personel beyanlı geçiş yaparken telefonun GPS konum iznini açmak ve koordinat göndermek zorundadır.'
                                    : 'Opsiyonel: Konum izni kapalı olsa dahi personel beyanlı giriş/çıkış kaydı gönderebilir.'}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-1.5 bg-gray-200/80 dark:bg-[#18191c] p-1 rounded-xl border border-gray-200 dark:border-gray-800">
                        <button
                            type="button"
                            onClick={() => handleSetLocation(false)}
                            disabled={loading || !canManageSettings || !enabled}
                            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                !requireLocation
                                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm border border-gray-200/80 dark:border-gray-700'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                            } ${(!canManageSettings || !enabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            Hayır (Opsiyonel)
                        </button>
                        <button
                            type="button"
                            onClick={() => handleSetLocation(true)}
                            disabled={loading || !canManageSettings || !enabled}
                            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                requireLocation
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                            } ${(!canManageSettings || !enabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            Evet (GPS Zorunlu)
                        </button>
                    </div>
                </div>
            </div>

            {enabled && !requireLocation && (
                <div className="mt-3 p-3 rounded-xl bg-amber-50/80 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-800/40 flex items-center gap-2.5 text-xs text-amber-800 dark:text-amber-300">
                    <ShieldAlert className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-400" />
                    <span>
                        <strong>Güvenlik Önerisi:</strong> Beyanlı geçiş aktifken suistimalleri önlemek için <strong>GPS Konum Zorunluluğu</strong> ayarını <em>Evet (GPS Zorunlu)</em> konumunda tutmanız önerilir.
                    </span>
                </div>
            )}
        </div>
    );
}
